import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { statsAPI, linksAPI, subscriptionAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Link2, CreditCard, Shield, Activity, UserCheck, MessageSquare, Smartphone } from 'lucide-react';
import { ROLE_COLORS, ROLE_LABELS } from '@/components/AppLayout';

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [myLinks, setMyLinks] = useState([]);
  const [mobileLinks, setMobileLinks] = useState({ app_store_url: '', play_store_url: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes] = await Promise.all([statsAPI.get()]);
      setStats(statsRes.data);
    } catch { /* ignore */ }
    try {
      if (user?.role === 'user' || user?.role === 'integrator') {
        const linksRes = await linksAPI.getAll();
        setMyLinks(linksRes.data);
      }
    } catch { /* ignore */ }
    try {
      const mobileRes = await subscriptionAPI.getMobileLinks();
      setMobileLinks(mobileRes.data);
    } catch { /* ignore */ }
  };

  const role = user?.role;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Bentornato, <span className="text-foreground font-medium">{user?.name}</span>
          </p>
        </div>
        <Badge className={`text-sm ${ROLE_COLORS[role]}`} data-testid="user-role-badge">
          {ROLE_LABELS[role]}
        </Badge>
      </div>

      {/* Root/Admin Stats */}
      {(role === 'root' || role === 'administrator') && stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            testId="stat-users"
            icon={Users}
            label="Utenti Totali"
            value={stats.total_users}
            color="text-blue-500"
          />
          <StatCard
            testId="stat-links"
            icon={Link2}
            label="OBC Totali"
            value={stats.total_links}
            color="text-purple-500"
          />
          <StatCard
            testId="stat-subs"
            icon={CreditCard}
            label="Abbonamenti Attivi"
            value={stats.active_subscriptions}
            color="text-green-500"
          />
          <StatCard
            testId="stat-integrators"
            icon={UserCheck}
            label="Integratori"
            value={stats.total_integrators}
            color="text-orange-500"
          />
          <StatCard
            testId="stat-tickets"
            icon={MessageSquare}
            label="Ticket Aperti"
            value={stats.open_tickets}
            color="text-yellow-500"
          />
        </div>
      )}

      {/* Integrator Stats */}
      {role === 'integrator' && stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            testId="stat-managed-users"
            icon={Users}
            label="Utenti Gestiti"
            value={stats.managed_users}
            color="text-purple-500"
          />
          <StatCard
            testId="stat-my-links"
            icon={Link2}
            label="I Miei OBC"
            value={stats.my_links}
            color="text-blue-500"
          />
          <StatCard
            testId="stat-managed-links"
            icon={Activity}
            label="OBC Gestiti"
            value={stats.managed_links}
            color="text-green-500"
          />
        </div>
      )}

      {/* User Stats */}
      {role === 'user' && stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StatCard
            testId="stat-my-links"
            icon={Link2}
            label="I Miei OBC"
            value={stats.my_links}
            color="text-blue-500"
          />
          <Card className="border-border bg-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Abbonamento</p>
                  <p className="text-lg font-semibold capitalize" data-testid="stat-subscription-status">
                    {user?.subscription_status === 'trial' ? 'Prova Gratuita' : 'Attivo'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Links for User */}
      {(role === 'user' || role === 'integrator') && myLinks.length > 0 && (
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
              OBC Rapidi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {myLinks.slice(0, 6).map((link) => (
                <a
                  key={link.link_id}
                  href={`/view/${link.link_id}`}
                  data-testid={`quick-link-${link.link_id}`}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Link2 className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-sm font-medium truncate">{link.name}</span>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info card for root */}
      {role === 'root' && (
        <Card className="border-border bg-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="font-semibold mb-1" style={{ fontFamily: 'Manrope' }}>Accesso Root</h3>
                <p className="text-sm text-muted-foreground">
                  Hai accesso completo a tutte le funzionalita' del sistema. Puoi gestire utenti, assegnare ruoli, 
                  creare OBC e gestire gli abbonamenti di tutti gli utenti.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mobile App Download Card */}
      {(mobileLinks.app_store_url || mobileLinks.play_store_url) && (
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Smartphone className="w-5 h-5 text-blue-500" />
              Scarica l'App Mobile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Gestisci i tuoi OBC ovunque ti trovi con la nostra app mobile.
            </p>
            <div className="flex flex-wrap gap-3">
              {mobileLinks.app_store_url && (
                <a
                  href={mobileLinks.app_store_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-ios-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  App Store
                </a>
              )}
              {mobileLinks.play_store_url && (
                <a
                  href={mobileLinks.play_store_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-android-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  Google Play
                </a>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <Card className="border-border bg-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-lg ${color.replace('text-', 'bg-').replace('500', '500/10')} flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${color}`} />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold" data-testid={testId}>{value ?? 0}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
